import React, { useRef, useState, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { FrameLabel } from "../common/ObjectTypes";
import ReviewedBadge, { REVIEWED_IMAGE_FILTER } from "./ReviewedBadge";
import {
  LABEL_OFFSET_EM,
  labelOffsetPx,
  resolveLabelAnchor,
} from "./boundingBoxLabel";

// Fallback until the container's computed font size can be measured, and the
// browser default it almost always resolves to.
const DEFAULT_FONT_SIZE_PX = 16;

const hexToRgba = (hex: string, alpha: number): string => {
  const h = hex.replace("#", "");
  const norm =
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h;
  const r = parseInt(norm.substring(0, 2), 16);
  const g = parseInt(norm.substring(2, 4), 16);
  const b = parseInt(norm.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export interface BoundingBox {
  id?: string; // stable identity for click/selection (e.g. String(prediction_id))
  x: number; // left (px or %)
  y: number; // top (px or %)
  width: number; // (px or %)
  height: number; // (px or %)
  label?: string;
  color?: string;
  confidence?: number; // 0-1 or 0-100
  // AKL-731 (reject-reveal follow-up): a "rejected" box is hidden by default
  // (see the render filter below) even with no card ↔ box highlight active
  // elsewhere -- it only renders when it's itself the highlighted box (the
  // reviewer clicked/hovered its card in the "Rejected predictions" panel).
  visibility?: string;
}

/**
 * Where a box interaction happened, for anchoring a popover. A `MouseEvent`
 * satisfies it directly; a keyboard activation (AKL-717) supplies the box's
 * centre instead, so the caller never has to know which input opened the box.
 */
export type BoxClickAnchor = {
  clientX: number;
  clientY: number;
  /**
   * True when the box was activated from the keyboard or by assistive
   * technology (the browser synthesises a click with no pointer position), so
   * a caller that swallows the click ending a mouse drag knows this is not
   * that click (Copilot, web#775 round 6). Absent for a real mouse event.
   */
  viaKeyboard?: boolean;
};

interface BoundingBoxOverlayProps {
  imageUrl: string;
  imageAlt?: string;
  boxes: BoundingBox[];
  frameLabels?: FrameLabel[];
  frameLabelSize?: "default" | "large";
  boxStyle?: React.CSSProperties;
  usePercent?: boolean; // If true, coordinates are in %
  // Optional CSS `filter` chain applied to the underlying <img> only (not the
  // box <div>s), so a caller (e.g. ImageCanvas) can compose brightness/sharpen
  // filters on the main image without altering box coordinates or hit-test math.
  imageFilter?: string;
  onClick?: (
    event: React.MouseEvent<
      HTMLDivElement | HTMLImageElement | HTMLButtonElement,
      MouseEvent
    >,
    imageUrl: string,
  ) => void;
  // Fires with the identity of the box under the cursor (reusing the same
  // smallest-box hit-test as hover), or null when the click missed every box.
  // AKL-717: also fired from the keyboard — Enter / Space on a focused box —
  // with the box's centre as the anchor. Wiring this makes every box a real,
  // keyboard-operable control: an unresolved box has no card, so the box is
  // the only way to reach Assign species / Reject for it.
  onBoxClick?: (boxId: string | null, anchor: BoxClickAnchor) => void;
  // Persisted selection, distinct from transient hover: the matching box stays
  // highlighted (e.g. while a popover is open) regardless of cursor position.
  selectedBoxId?: string | null;
  // AKL-698: card ↔ bbox linkage. When the reviewer hovers or clicks an
  // observation / suggestion card in the side panel, the surface passes the
  // ids of every box that card owns and this overlay layers a distinct outline
  // on them. Separate from `selectedBoxId` (which drives the popover anchor)
  // so card interactions don't open the popover.
  highlightedBoxIds?: string[];
  // AKL-698: when false, box labels (species + confidence chip above each box)
  // are not rendered. Used by the "Show labels + confidence" toggle that
  // appears in the review surface while a card is pointer-selected. Defaults
  // to true so existing callers (grid tile, filmstrip) render unchanged.
  showLabels?: boolean;
  // AKL-696: when true, mark this tile as reviewed — dims/desaturates the image
  // (boxes stay crisp) and overlays a check badge. Additive; defaults to false so
  // existing callers (detail overlay, filmstrip box overlay) render unchanged.
  reviewed?: boolean;
  // Which corner the reviewed badge pins to. Defaults to the badge's own
  // top-left, so every existing caller renders unchanged; the review grid moves
  // it out of the way when bulk-select mode puts a checkbox in that corner
  // (AKL-753).
  reviewedBadgePosition?:
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right";
  // Counter-scale applied to box labels so their text stays a constant on-screen
  // size when an ancestor (ImageCanvas) zooms the whole overlay. Pass 1/zoom;
  // defaults to 1 (no counter-scaling, e.g. in the grid tiles).
  labelScale?: number;
  className?: string;
  loading?: "eager" | "lazy";
  fetchPriority?: "high" | "low" | "auto";
  style?: React.CSSProperties;
}

/**
 * Component to display an image with bounding boxes overlaid.
 */
const BoundingBoxOverlay: React.FC<BoundingBoxOverlayProps> = ({
  imageUrl,
  imageAlt = "",
  boxes,
  frameLabels,
  frameLabelSize = "default",
  boxStyle = {},
  usePercent = false,
  onClick,
  onBoxClick,
  selectedBoxId,
  highlightedBoxIds,
  showLabels = true,
  reviewed = false,
  reviewedBadgePosition,
  labelScale = 1,
  className,
  loading,
  fetchPriority,
  style,
  imageFilter,
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [imgDims, setImgDims] = useState({
    width: 1,
    height: 1,
    naturalWidth: 1,
    naturalHeight: 1,
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [hoveredBox, setHoveredBox] = useState<number | null>(null);
  const [labelFontSizePx, setLabelFontSizePx] = useState(DEFAULT_FONT_SIZE_PX);

  // Measure the font size the labels inherit so the top-edge flip threshold
  // (AKL-705) is expressed in the same px space as the box geometry. Read once:
  // the surrounding typography doesn't change for this component's lifetime.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measured = parseFloat(window.getComputedStyle(container).fontSize);
    if (Number.isFinite(measured) && measured > 0) {
      setLabelFontSizePx(measured);
    }
  }, []);

  useLayoutEffect(() => {
    setImageLoaded(false);
    const img = imgRef.current;
    if (!img) return;

    const updateDims = () => {
      setImgDims({
        width: img.width,
        height: img.height,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
      });
    };

    updateDims();
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateDims);
      observer.observe(img);
      return () => {
        observer.disconnect();
      };
    } else {
      window.addEventListener("resize", updateDims);
      return () => {
        window.removeEventListener("resize", updateDims);
      };
    }
  }, [imageUrl]);

  // Calculate scale factors
  const scaleX = imgDims.width / imgDims.naturalWidth;
  const scaleY = imgDims.height / imgDims.naturalHeight;

  // Sort once so the hover hit-test and the render use the same indices.
  // Larger boxes first means smaller (or enclosed) boxes stack later in DOM,
  // which we keep for visual layering. Hit-testing uses an explicit "smallest
  // box containing the cursor" rule below so sibling-overlap event quirks
  // do not leave an outer box stuck in the hovered state.
  const sortedBoxes = [...boxes].sort(
    (a, b) => b.width * b.height - a.width * a.height,
  );

  // Shared "smallest box containing the point" hit-test, in client coordinates.
  // Returns the index into `sortedBoxes`, or null when the point is over no box.
  // Both hover and click reuse this so a click identifies the exact box the
  // hover visual would highlight.
  const hitTestBox = (clientX: number, clientY: number): number | null => {
    const container = containerRef.current;
    if (!container) return null;
    const rect = container.getBoundingClientRect();
    // getBoundingClientRect reports the *visual* (post-CSS-transform) size, while
    // box coordinates are in the element's *layout* space. Divide by the visual
    // scale so hit-testing stays accurate when an ancestor (e.g. ImageCanvas)
    // applies a zoom transform. With no transform the factor is 1 (no-op).
    const scaleFactorX = container.offsetWidth
      ? rect.width / container.offsetWidth
      : 1;
    const scaleFactorY = container.offsetHeight
      ? rect.height / container.offsetHeight
      : 1;
    const cursorX = (clientX - rect.left) / scaleFactorX;
    const cursorY = (clientY - rect.top) / scaleFactorY;

    let bestIdx: number | null = null;
    let bestArea = Infinity;
    for (let i = 0; i < sortedBoxes.length; i++) {
      const b = sortedBoxes[i];
      const bLeft = usePercent ? (b.x / 100) * imgDims.width : b.x * scaleX;
      const bTop = usePercent ? (b.y / 100) * imgDims.height : b.y * scaleY;
      const bWidth = usePercent
        ? (b.width / 100) * imgDims.width
        : b.width * scaleX;
      const bHeight = usePercent
        ? (b.height / 100) * imgDims.height
        : b.height * scaleY;
      if (
        cursorX >= bLeft &&
        cursorX <= bLeft + bWidth &&
        cursorY >= bTop &&
        cursorY <= bTop + bHeight
      ) {
        const area = bWidth * bHeight;
        if (area < bestArea) {
          bestArea = area;
          bestIdx = i;
        }
      }
    }
    return bestIdx;
  };

  const handleHoverMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!imageLoaded) return;
    const bestIdx = hitTestBox(e.clientX, e.clientY);
    if (bestIdx !== hoveredBox) {
      setHoveredBox(bestIdx);
    }
  };

  const { t } = useTranslation();
  // AKL-717: with `onBoxClick` wired the boxes themselves are the controls —
  // native <button>s, so they are focusable, named for a screen reader, and
  // activate on Enter / Space with no custom key handling.
  const boxesAreButtons = !!onBoxClick;

  const handleClick =
    onClick || onBoxClick
      ? (
          e: React.MouseEvent<
            HTMLDivElement | HTMLImageElement | HTMLButtonElement,
            MouseEvent
          >,
        ) => {
          // Clear hover state on click — the cursor stays put when a modal
          // opens over the grid, so mouseLeave never fires and the box would
          // otherwise stay stuck in its hovered visual under the modal.
          setHoveredBox(null);
          if (onBoxClick) {
            const idx = hitTestBox(e.clientX, e.clientY);
            onBoxClick(idx != null ? (sortedBoxes[idx].id ?? null) : null, e);
          }
          onClick?.(e, imageUrl);
        }
      : undefined;

  // AKL-717: a box button's click. A pointer click carries a position and takes
  // the same smallest-box hit test as before (nested boxes resolve to the
  // innermost, whatever element the browser targeted). A keyboard or assistive-
  // technology activation arrives as a click with `detail === 0` and no useful
  // position, so it names this box directly and anchors on its centre.
  const activateBox =
    (box: BoundingBox) =>
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      if (e.detail !== 0) {
        handleClick?.(e);
        return;
      }
      setHoveredBox(null);
      const rect = e.currentTarget.getBoundingClientRect();
      onBoxClick?.(box.id ?? null, {
        clientX: rect.left + rect.width / 2,
        clientY: rect.top + rect.height / 2,
        viaKeyboard: true,
      });
    };

  // The overlay acts as a clickable tile only when a caller wires `onClick`
  // WITHOUT `onBoxClick` (the review-feed grid). In that mode it must be a real,
  // keyboard-operable control; when `onBoxClick` is set (the detail overlay) the
  // container stays a plain element and the per-box controls — real buttons
  // since AKL-717, see `boxesAreButtons` — handle interaction.
  const isTileButton = !!onClick && !onBoxClick;

  const handleTileKeyDown = isTileButton
    ? (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault(); // Space would otherwise scroll the page
          // Fire the image's real click path so `onClick` (via handleClick)
          // receives a genuine MouseEvent — no keyboard→mouse event cast.
          imgRef.current?.click();
        }
      }
    : undefined;

  // Compose the caller's image filter with the reviewed dim (AKL-696). Applied
  // to the <img> only, so bounding boxes and the check badge stay unaffected.
  const composedImageFilter =
    [imageFilter, reviewed ? REVIEWED_IMAGE_FILTER : null]
      .filter(Boolean)
      .join(" ") || undefined;

  // How far a label overhangs its box's top edge, before the zoom counter-scale.
  const labelOffset = labelOffsetPx(labelFontSizePx);

  return (
    <div
      ref={containerRef}
      // isolation: isolate makes this overlay its own stacking context, so the
      // reviewed badge (z-[1001]) and active boxes (zIndex 1000) stay contained
      // within the tile and can't paint over higher-level UI like the full-screen
      // review modal (z-50) when the grid is still mounted behind it.
      style={{
        position: "relative",
        display: "inline-block",
        isolation: "isolate",
      }}
      // Keyboard-operable tile only in grid mode (isTileButton); the accessible
      // name comes from the <img> alt the caller passes.
      role={isTileButton ? "button" : undefined}
      tabIndex={isTileButton ? 0 : undefined}
      onKeyDown={handleTileKeyDown}
      className={
        isTileButton
          ? "rounded focus-visible:ring-2 focus-visible:ring-sky-blue-light focus:outline-none"
          : undefined
      }
      onMouseMove={handleHoverMove}
      onMouseLeave={() => setHoveredBox(null)}
    >
      <img
        ref={imgRef}
        src={imageUrl}
        alt={imageAlt}
        className={className}
        loading={loading}
        fetchPriority={fetchPriority}
        decoding="async"
        // Disable the browser's native image drag so click-drag pans the canvas
        // (via ImageCanvas) instead of starting a drag-and-drop ghost image.
        draggable={false}
        style={{
          display: "block",
          width: "100%",
          height: "auto",
          ...style,
          ...(composedImageFilter ? { filter: composedImageFilter } : {}),
        }}
        onLoad={async () => {
          const img = imgRef.current;
          if (!img) return;

          try {
            if ("decode" in img && typeof img.decode === "function") {
              await img.decode();
            }
          } catch {
            // Ignore decode failures and continue using the loaded image data.
          }

          // Guard: bail if unmounted or if src changed mid-decode (naturalWidth=0 means new image not yet loaded)
          if (!imgRef.current || !img.naturalWidth) return;

          setImgDims({
            width: img.width,
            height: img.height,
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight,
          });
          setHoveredBox(null);
          setImageLoaded(true);
        }}
        onClick={handleClick}
      />
      {reviewed && <ReviewedBadge position={reviewedBadgePosition} />}
      {imageLoaded &&
        sortedBoxes.map((box, idx) => {
          // AKL-698: while a card ↔ bbox highlight is active, hide every box
          // that isn't part of the highlight (both the box outline and its
          // label) so the reviewer focuses on the linked detection(s).
          const highlightActive =
            !!highlightedBoxIds && highlightedBoxIds.length > 0;
          // Only scan the highlight set when one is actually active — skips the
          // per-box `includes()` on the common no-highlight path.
          const inHighlightSet =
            highlightActive &&
            box.id != null &&
            !!highlightedBoxIds &&
            highlightedBoxIds.includes(box.id);
          // AKL-731 (reject-reveal follow-up): a rejected box is hidden by
          // default -- unlike the highlight rule above, this applies even
          // with no highlight active anywhere else. It only renders when
          // it's itself in the highlight set, i.e. the reviewer selected its
          // own card (grid usage never sets highlightedBoxIds at all, so a
          // rejected box there is always hidden -- exactly the "grid/strip
          // never reveal it" requirement).
          if (
            (highlightActive || box.visibility === "rejected") &&
            !inHighlightSet
          ) {
            return null;
          }
          let left, top, width, height;
          let renderedTopPx: number;
          let renderedLeftPx: number;
          let renderedWidthPx: number;
          if (usePercent) {
            left = `${box.x}%`;
            top = `${box.y}%`;
            width = `${box.width}%`;
            height = `${box.height}%`;
            renderedTopPx = (box.y / 100) * imgDims.height;
            renderedLeftPx = (box.x / 100) * imgDims.width;
            renderedWidthPx = (box.width / 100) * imgDims.width;
          } else {
            left = box.x * scaleX;
            top = box.y * scaleY;
            width = box.width * scaleX;
            height = box.height * scaleY;
            renderedTopPx = top;
            renderedLeftPx = left;
            renderedWidthPx = width;
          }
          const isHovered = hoveredBox === idx;
          const isSelected =
            selectedBoxId != null && box.id != null && box.id === selectedBoxId;
          const isActive = isHovered || isSelected || inHighlightSet;
          const borderColor = box.color || "#FF0000";
          // Border and outline widths counter-scale with the ancestor zoom
          // transform (same 1/zoom factor labels already use) so their on-
          // screen thickness stays constant no matter how far the reviewer
          // zooms in. Selected-box shadow is likewise sized in unit-of-zoom.
          const activeBorderPx = 2 * labelScale;
          const idleBorderPx = 1 * labelScale;
          const outlinePx = 1 * labelScale;
          const selectedRingPx = 2 * labelScale;
          const selectedOuterPx = 4 * labelScale;
          const selectedShadow = `0 0 0 ${selectedRingPx}px #ffffff, 0 0 0 ${selectedOuterPx}px ${borderColor}`;
          const style: React.CSSProperties = {
            position: "absolute",
            left,
            top,
            width,
            height,
            border: `${
              isActive ? activeBorderPx : idleBorderPx
            }px solid ${borderColor}`,
            // A persisted white/colored ring marks the selected box distinctly
            // from the hover tint and from the box's taxon/review color.
            outline: `${outlinePx}px solid rgba(0, 0, 0, 0.6)`,
            // AKL-698: no extra glow on highlighted boxes — since every non-
            // highlighted box is hidden while a card is engaged, the box's
            // continued presence is itself the highlight signal.
            boxShadow: isSelected ? selectedShadow : undefined,
            boxSizing: "border-box",
            backgroundColor: isActive
              ? hexToRgba(borderColor, 0.15)
              : "transparent",
            pointerEvents: "auto",
            cursor: onClick || onBoxClick ? "pointer" : "default",
            zIndex: isActive ? 1000 : 1,
            ...boxStyle,
          };

          // AKL-698: labels sit above the box's top edge by default, so they
          // never occlude the detection, and anchor to the right when the box
          // is near the right edge so a long label does not spill out past the
          // image's right side. AKL-705: a box too close to the image's top
          // flips its label to just inside the box instead — above it, the
          // label would fall outside the image and be clipped by the
          // surrounding overflow-hidden container, making it unreadable.
          const { anchorRight, anchorTop } = resolveLabelAnchor({
            boxLeftPx: renderedLeftPx,
            boxTopPx: renderedTopPx,
            boxWidthPx: renderedWidthPx,
            imageWidthPx: imgDims.width,
            offsetPx: labelOffset,
            labelScale,
          });
          const labelStyle: React.CSSProperties = {
            position: "absolute",
            top: anchorTop ? 0 : `-${LABEL_OFFSET_EM}em`,
            background: box.color || "#FF0000",
            color: "#1F2937",
            padding: "2px 6px",
            fontSize: "0.85em",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            gap: "0.5em",
            pointerEvents: "auto" as const,
            cursor: "pointer",
            whiteSpace: "nowrap",
            // Counter the ancestor zoom so the label keeps a constant on-screen
            // size (anchored to the box corner it sits against). The vertical
            // origin follows the flip, so a flipped label grows down into the
            // box rather than up off the top of it.
            ...(labelScale !== 1
              ? {
                  transform: `scale(${labelScale})`,
                  transformOrigin: `${anchorRight ? "right" : "left"} ${
                    anchorTop ? "top" : "bottom"
                  }`,
                }
              : {}),
            ...(anchorRight ? { right: 0 } : { left: 0 }),
          };

          const content = showLabels &&
            (box.label || box.confidence !== undefined) && (
              <span style={labelStyle}>
                {box.label && <span>{box.label}</span>}
                {box.confidence !== undefined && (
                  <span>
                    {typeof box.confidence === "number"
                      ? `${(box.confidence <= 1
                          ? box.confidence * 100
                          : box.confidence
                        ).toFixed(0)}%`
                      : box.confidence}
                  </span>
                )}
              </span>
            );
          if (boxesAreButtons) {
            // AKL-717: a real <button>, as the project's accessibility rule
            // requires — an unresolved box has no card, so the box is the only
            // way to reach Assign species / Reject, and it has to work from the
            // keyboard. The accessible name says what the box is; a box with no
            // label (unresolved, or a revealed rejection) must not borrow one.
            // The review surface's own shortcuts already ignore Enter / Space
            // on a focused BUTTON, so activating a box never also saves.
            return (
              <button
                key={idx}
                type="button"
                style={{
                  ...style,
                  // Reset the user-agent button chrome; every visual the box
                  // has comes from `style` above.
                  padding: 0,
                  margin: 0,
                  font: "inherit",
                  color: "inherit",
                  textAlign: "inherit",
                  appearance: "none",
                  WebkitAppearance: "none",
                }}
                className="focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1"
                aria-label={
                  box.label
                    ? t("Detection: {{label}}", { label: box.label })
                    : t("Unlabelled detection")
                }
                onClick={activateBox(box)}
                data-highlighted={inHighlightSet ? "true" : undefined}
              >
                {content}
              </button>
            );
          }
          return (
            <div
              key={idx}
              style={style}
              onClick={handleClick}
              data-highlighted={inHighlightSet ? "true" : undefined}
            >
              {content}
            </div>
          );
        })}
      {imageLoaded && frameLabels && frameLabels.length > 0 && (
        <div
          data-testid="frame-labels-container"
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 4,
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          {frameLabels.map((fl) => (
            <span
              key={fl.prediction_id}
              data-testid="frame-label-badge"
              className={
                frameLabelSize === "large"
                  ? "text-sm px-3 py-1.5 rounded shadow"
                  : "text-xs px-2 py-1 rounded shadow"
              }
              style={{ backgroundColor: "#E6C200", color: "#1F2937" }}
            >
              {fl.label}
              {fl.confidence != null && (
                <span className="ml-1 opacity-80">
                  {`${(fl.confidence <= 1
                    ? fl.confidence * 100
                    : fl.confidence
                  ).toFixed(0)}%`}
                </span>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default BoundingBoxOverlay;
